import * as React from 'react';
import { useEvent } from '@rc-component/util';
import { clsx } from 'clsx';
import scrollIntoView from 'scroll-into-view-if-needed';
import getScroll from '../_util/getScroll';
import { useMergeSemantic, useSemanticRootStyle } from '../_util/hooks/useMergeSemantic';
import { isFunction, isNumber, isPlainObject } from '../_util/is';
import scrollTo from '../_util/scrollTo';
import { devUseWarning } from '../_util/warning';
import Affix from '../affix';
import { ConfigContext, useComponentConfig } from '../config-provider/context';
import useCSSVarCls from '../config-provider/hooks/useCSSVarCls';
import AnchorLink from './AnchorLink';
import AnchorContext from './context';
import useStyle from './style';
function getDefaultContainer() {
    return window;
}
function getOffsetTop(element, container) {
    if (!element.getClientRects().length) {
        return 0;
    }
    const rect = element.getBoundingClientRect();
    if (rect.width || rect.height) {
        if (container === window) {
            return rect.top - element.ownerDocument.documentElement.clientTop;
        }
        return rect.top - container.getBoundingClientRect().top;
    }
    return rect.top;
}
const sharpMatcherRegex = /#([^\t\r\n\f\v]+)$/;
const Anchor = (props) => {
    const { rootClassName, prefixCls: customPrefixCls, className, style, offsetTop, affix = true, showInkInFixed = false, children, items, direction: anchorDirection = 'vertical', bounds, targetOffset, onClick, onChange, getContainer, getCurrentAnchor, replace, classNames, styles, } = props;
    // =================== Warning =====================
    if (process.env.NODE_ENV !== 'production') {
        const warning = devUseWarning('Anchor');
        warning.deprecated(!children, 'Anchor children', 'items');
        warning(!(anchorDirection === 'horizontal' && items?.some((n) => 'children' in n)), 'usage', '`Anchor items#children` is not supported when `Anchor` direction is horizontal.');
    }
    const [links, setLinks] = React.useState([]);
    const [activeLink, setActiveLink] = React.useState(null);
    const activeLinkRef = React.useRef(activeLink);
    const rawActiveLinkRef = React.useRef(activeLink);
    const wrapperRef = React.useRef(null);
    const spanLinkNodeRef = React.useRef(null);
    const animatingRef = React.useRef(false);
    const scrollRequestIdRef = React.useRef(null);
    const linkTargetOffsetRef = React.useRef({});
    const { direction, getPrefixCls, className: contextClassName, style: contextStyle, classNames: contextClassNames, styles: contextStyles, } = useComponentConfig('anchor');
    const { getTargetContainer } = React.useContext(ConfigContext);
    const prefixCls = getPrefixCls('anchor', customPrefixCls);
    const rootCls = useCSSVarCls(prefixCls);
    const [hashId, cssVarCls] = useStyle(prefixCls, rootCls);
    const getCurrentContainer = getContainer ?? getTargetContainer ?? getDefaultContainer;
    const dependencyListItem = JSON.stringify(links);
    const registerLink = (link, newTargetOffset) => {
        setLinks((prev) => {
            if (!prev.includes(link)) {
                return [...prev, link];
            }
            return prev;
        });
        // Store link-level targetOffset for scroll detection
        if (newTargetOffset !== undefined) {
            linkTargetOffsetRef.current[link] = newTargetOffset;
        }
    };
    const unregisterLink = (link) => {
        setLinks((prev) => prev.filter((i) => i !== link));
        // Clean up targetOffset when unregistering
        delete linkTargetOffsetRef.current[link];
    };
    const updateInk = () => {
        const linkNode = wrapperRef.current?.querySelector(`.${prefixCls}-link-title-active`);
        if (linkNode && spanLinkNodeRef.current) {
            const { style: inkStyle } = spanLinkNodeRef.current;
            const horizontalAnchor = anchorDirection === 'horizontal';
            inkStyle.top = horizontalAnchor ? '' : `${linkNode.offsetTop + linkNode.clientHeight / 2}px`;
            inkStyle.height = horizontalAnchor ? '' : `${linkNode.clientHeight}px`;
            inkStyle.left = horizontalAnchor ? `${linkNode.offsetLeft}px` : '';
            inkStyle.width = horizontalAnchor ? `${linkNode.clientWidth}px` : '';
            if (horizontalAnchor) {
                scrollIntoView(linkNode, { scrollMode: 'if-needed', block: 'nearest' });
            }
        }
    };
    const getInternalCurrentAnchor = (_links, _offsetTop, _bounds = 5, _linkTargetOffset) => {
        const linkSections = [];
        const container = getCurrentContainer();
        _links.forEach((link) => {
            const sharpLinkMatch = sharpMatcherRegex.exec(link?.toString());
            if (!sharpLinkMatch) {
                return;
            }
            const target = document.getElementById(sharpLinkMatch[1]);
            if (target) {
                // Use link-level targetOffset if provided, otherwise use global offsetTop
                const linkOffsetTop = _linkTargetOffset?.[link] ?? _offsetTop;
                const top = getOffsetTop(target, container);
                if (top <= linkOffsetTop + _bounds) {
                    linkSections.push({ link, top });
                }
            }
        });
        if (linkSections.length) {
            const maxSection = linkSections.reduce((prev, curr) => (curr.top > prev.top ? curr : prev));
            return maxSection.link;
        }
        return '';
    };
    const setCurrentActiveLink = useEvent((link, forceTriggerChange = false) => {
        rawActiveLinkRef.current = link;
        // https://github.com/ant-design/ant-design/issues/30584
        const newLink = isFunction(getCurrentAnchor) ? getCurrentAnchor(link) : link;
        const isSameLink = activeLinkRef.current === newLink;
        if (isSameLink && !forceTriggerChange) {
            return;
        }
        if (!isSameLink) {
            setActiveLink(newLink);
            activeLinkRef.current = newLink;
        }
        // onChange should respect the original link (which may caused by
        // window scroll or user click), not the new link
        onChange?.(link);
    });
    const handleScroll = React.useCallback(() => {
        if (animatingRef.current) {
            return;
        }
        const currentActiveLink = getInternalCurrentAnchor(links, isNumber(targetOffset) ? targetOffset : offsetTop || 0, bounds, linkTargetOffsetRef.current);
        setCurrentActiveLink(currentActiveLink);
    }, [links, targetOffset, offsetTop, bounds]);
    const handleScrollTo = React.useCallback((link, targetOffsetParams) => {
        const previousRawActiveLink = rawActiveLinkRef.current;
        setCurrentActiveLink(link, previousRawActiveLink !== link);
        const sharpLinkMatch = sharpMatcherRegex.exec(link);
        if (!sharpLinkMatch) {
            return;
        }
        const targetElement = document.getElementById(sharpLinkMatch[1]);
        if (!targetElement) {
            return;
        }
        if (animatingRef.current) {
            if (previousRawActiveLink === link) {
                return;
            }
            scrollRequestIdRef.current?.();
        }
        const container = getCurrentContainer();
        const scrollTop = getScroll(container);
        const eleOffsetTop = getOffsetTop(targetElement, container);
        let y = scrollTop + eleOffsetTop;
        const finalTargetOffset = targetOffsetParams ?? targetOffset ?? offsetTop ?? 0;
        y -= finalTargetOffset;
        animatingRef.current = true;
        scrollRequestIdRef.current = scrollTo(y, {
            getContainer: getCurrentContainer,
            callback() {
                animatingRef.current = false;
            },
        });
    }, [targetOffset, offsetTop]);
    // =========== Merged Props for Semantic ==========
    const mergedProps = {
        ...props,
        direction: anchorDirection,
    };
    const contextStyleRoot = useSemanticRootStyle(contextStyle);
    const styleRoot = useSemanticRootStyle(style);
    const [mergedClassNames, mergedStyles] = useMergeSemantic([contextClassNames, classNames], [contextStyles, contextStyleRoot, styles, styleRoot], {
        props: mergedProps,
    });
    const wrapperClass = clsx(hashId, cssVarCls, rootCls, rootClassName, `${prefixCls}-wrapper`, {
        [`${prefixCls}-wrapper-horizontal`]: anchorDirection === 'horizontal',
        [`${prefixCls}-rtl`]: direction === 'rtl',
    }, className, contextClassName, mergedClassNames.root);
    const anchorClass = clsx(prefixCls, {
        [`${prefixCls}-fixed`]: !affix && !showInkInFixed,
    });
    const inkClass = clsx(`${prefixCls}-ink`, mergedClassNames.indicator, {
        [`${prefixCls}-ink-visible`]: activeLink,
    });
    const wrapperStyle = {
        maxHeight: offsetTop ? `calc(100vh - ${offsetTop}px)` : '100vh',
        ...mergedStyles.root,
    };
    const createNestedLink = (options) => Array.isArray(options)
        ? options.map((item) => (React.createElement(AnchorLink, { replace: replace, ...item, key: item.key }, anchorDirection === 'vertical' && createNestedLink(item.children))))
        : null;
    const anchorContent = (React.createElement("div", { ref: wrapperRef, className: wrapperClass, style: wrapperStyle },
        React.createElement("div", { className: anchorClass },
            React.createElement("span", { className: inkClass, ref: spanLinkNodeRef, style: mergedStyles.indicator }),
            'items' in props ? createNestedLink(items) : children)));
    React.useEffect(() => {
        const scrollContainer = getCurrentContainer();
        handleScroll();
        scrollContainer?.addEventListener('scroll', handleScroll);
        return () => {
            scrollContainer?.removeEventListener('scroll', handleScroll);
        };
    }, [dependencyListItem]);
    React.useEffect(() => {
        if (isFunction(getCurrentAnchor)) {
            setCurrentActiveLink(rawActiveLinkRef.current || '');
        }
    }, [getCurrentAnchor]);
    React.useEffect(() => {
        updateInk();
    }, [anchorDirection, getCurrentAnchor, dependencyListItem, activeLink]);
    const memoizedContextValue = React.useMemo(() => ({
        registerLink,
        unregisterLink,
        scrollTo: handleScrollTo,
        activeLink,
        onClick,
        direction: anchorDirection,
        classNames: mergedClassNames,
        styles: mergedStyles,
    }), [activeLink, onClick, handleScrollTo, anchorDirection, mergedStyles, mergedClassNames]);
    const affixProps = isPlainObject(affix) ? affix : undefined;
    return (React.createElement(AnchorContext.Provider, { value: memoizedContextValue }, affix ? (React.createElement(Affix, { offsetTop: offsetTop, target: getCurrentContainer, ...affixProps }, anchorContent)) : (anchorContent)));
};
if (process.env.NODE_ENV !== 'production') {
    Anchor.displayName = 'Anchor';
}
export default Anchor;
